import React from 'react';
import { 
  Box,
  Typography,
  CircularProgress,
  Alert,
  TablePagination,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Grid,
  Tooltip,
  Button
} from '@mui/material';
import { Appointment, AppointmentStatus } from '../types/appointment.types';
import { format } from 'date-fns';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoIcon from '@mui/icons-material/Info';
import { deepPurple, green, blue, orange, red, grey } from '@mui/material/colors';

interface PaginationProps {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
  onPageChange?: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

interface AppointmentsListProps {
  appointments: Appointment[];
  loading: boolean;
  error: Error | null;
  pagination?: PaginationProps;
  onViewAppointment?: (appointment: Appointment) => void;
}

const AppointmentsList: React.FC<AppointmentsListProps> = ({
  appointments = [],
  loading = false,
  error = null,
  pagination: paginationProp = {
    totalPages: 1,
    onPageChange: () => {},
    onRowsPerPageChange: () => {}
  },
  onViewAppointment = () => {}
}: AppointmentsListProps) => {
  // Set up pagination with defaults - ensure consistent values
  const page = paginationProp.page ?? 0;
  const limit = paginationProp.limit ?? 10;
  const total = paginationProp.total ?? appointments.length;
  const totalPages = paginationProp.totalPages ?? Math.ceil(total / limit);
  const onPageChange = paginationProp.onPageChange ?? (() => {});
  const onRowsPerPageChange = paginationProp.onRowsPerPageChange ?? (() => {});

  // Handle page change with proper type checking
  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onPageChange(event, newPage);
  };
  
  // Handle rows per page change with proper type checking
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onRowsPerPageChange(event);
  };

  // Get status color
  const getStatusColor = (status: AppointmentStatus) => {
    switch (status) {
      case AppointmentStatus.SCHEDULED:
        return 'default';
      case AppointmentStatus.CONFIRMED:
        return 'primary';
      case AppointmentStatus.COMPLETED:
        return 'success';
      case AppointmentStatus.CANCELLED:
        return 'error';
      case AppointmentStatus.NO_SHOW:
        return 'warning';
      case AppointmentStatus.IN_PROGRESS:
        return 'info';
      default:
        return 'default';
    }
  };

  // Format date and time with proper type checking
  const formatDateTime = (dateString: string | Date) => {
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date');
      }
      return {
        date: format(date, 'MMM dd, yyyy'),
        time: format(date, 'h:mm a')
      };
    } catch (error) {
      console.error('Error formatting date:', error);
      return { date: 'N/A', time: 'N/A' };
    }
  };

  // Loading state
  if (loading && appointments.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Error loading appointments: {error.message}
      </Alert>
    );
  }

  // Empty state
  if (!loading && appointments.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <EventIcon color="disabled" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h6" color="textSecondary">
          No appointments found
        </Typography>
        <Typography variant="body2" color="textSecondary" mt={1}>
          Try adjusting your search or filter criteria
        </Typography>
      </Box>
    );
  }

  // Enhanced status information with icons and colors
  const getStatusInfo = (status: AppointmentStatus) => {
    const statusMap = {
      [AppointmentStatus.SCHEDULED]: { 
        label: 'Scheduled',
        color: 'default' as const,
        bgColor: grey[100],
        icon: <EventIcon fontSize="small" />
      },
      [AppointmentStatus.CONFIRMED]: { 
        label: 'Confirmed', 
        color: 'info' as const,
        bgColor: blue[50],
        icon: <EventIcon color="info" fontSize="small" />
      },
      [AppointmentStatus.ARRIVED]: { 
        label: 'Arrived', 
        color: 'primary' as const,
        bgColor: blue[50],
        icon: <PersonIcon color="primary" fontSize="small" />
      },
      [AppointmentStatus.IN_PROGRESS]: { 
        label: 'In Progress', 
        color: 'warning' as const,
        bgColor: orange[50],
        icon: <AccessTimeIcon color="warning" fontSize="small" />
      },
      [AppointmentStatus.COMPLETED]: { 
        label: 'Completed', 
        color: 'success' as const,
        bgColor: green[50],
        icon: <LocalHospitalIcon color="success" fontSize="small" />
      },
      [AppointmentStatus.CANCELLED]: { 
        label: 'Cancelled',
        color: 'error' as const,
        bgColor: red[50],
        icon: <InfoIcon color="error" fontSize="small" />
      },
      [AppointmentStatus.NO_SHOW]: { 
        label: 'No Show', 
        color: 'error' as const,
        bgColor: red[50],
        icon: <InfoIcon color="error" fontSize="small" />
      },
      [AppointmentStatus.RESCHEDULED]: { 
        label: 'Rescheduled', 
        color: 'secondary' as const,
        bgColor: deepPurple[50],
        icon: <EventIcon color="secondary" fontSize="small" />
      },
    };

    return statusMap[status] || { 
      label: status, 
      color: 'default' as const,
      bgColor: grey[100],
      icon: <InfoIcon fontSize="small" />
    };
  };

  // Handle view appointment with proper type checking
  const handleViewAppointment = (appointment: Appointment) => {
    if (onViewAppointment) {
      onViewAppointment(appointment);
    }
  };
  
  // Show error state with proper error handling
  if (error) {
    // Safely extract error message from unknown error type
    const getErrorMessage = (err: unknown): string => {
      if (err === null || err === undefined) return 'An unknown error occurred';
      if (typeof err === 'string') return err;
      if (typeof err === 'object' && 'message' in err && typeof err.message === 'string') {
        return err.message;
      }
      return 'Failed to load appointments';
    };
    
    const errorMessage = getErrorMessage(error);
      
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          action={
            <Button 
              color="inherit" 
              size="small" 
              onClick={() => window.location.reload()}
              sx={{ textTransform: 'none' }}
            >
              Retry
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      </Box>
    );
  }
  
  // Function to get doctor's full name with qualification
  const getDoctorInfo = (appointment: Appointment) => {
    if (!appointment.doctor) return 'Not assigned';
    
    const { firstName, lastName, designation, qualification, employeeType } = appointment.doctor;
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    
    return (
      <Box>
        <Typography variant="subtitle2" component="div">
          {fullName || 'Unnamed Doctor'}
        </Typography>
        {[designation, qualification, employeeType].filter(Boolean).length > 0 && (
          <Typography variant="caption" color="text.secondary" component="div">
            {[designation, qualification, employeeType].filter(Boolean).join(' • ')}
          </Typography>
        )}
      </Box>
    );
  };

  // Function to safely check if a value is a valid Date
  const isValidDate = (date: unknown): date is Date => {
    // Check if it's a Date object and not invalid
    if (date === null || typeof date !== 'object') return false;
    
    // Safely check for Date methods
    const dateObj = date as Record<string, unknown>;
    const hasGetTime = typeof dateObj.getTime === 'function';
    const hasToISOString = typeof dateObj.toISOString === 'function';
    
    // If it has Date methods, check if it's a valid date
    if (hasGetTime && hasToISOString) {
      try {
        const time = (date as Date).getTime();
        // Check if the date is valid (not NaN and not invalid date)
        return !isNaN(time) && isFinite(time);
      } catch (e) {
        return false;
      }
    }
    
    // If it's a string that can be parsed to a valid date
    if (typeof date === 'string') {
      const parsed = new Date(date);
      return !isNaN(parsed.getTime());
    }
    
    return false;
  };

  // Function to format date and time from startTime
  const formatAppointmentDateTime = (startTime: string | Date | null | undefined): React.ReactNode => {
    try {
      // Handle null/undefined case
      if (startTime === null || startTime === undefined) {
        return 'Time not specified';
      }
      
      // Create date object from string or use as is if already a Date
      let date: Date;
      if (typeof startTime === 'string') {
        // Handle empty or whitespace-only strings
        if (!startTime.trim()) return 'Invalid date';
        date = new Date(startTime);
      } else {
        date = startTime;
      }
      
      // Check if date is valid
      if (!isValidDate(date)) return 'Invalid date';
      
      // Format as "09/07/2023 • 2:30 PM"
      return (
        <Box>
          <Typography variant="subtitle2" component="div">
            {format(date, 'dd/MM/yyyy')}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {format(date, 'h:mm a')}
          </Typography>
        </Box>
      );
    } catch (e) {
      // Safely handle any errors
      const error = e as Error;
      console.error('Error formatting date:', error.message);
      return 'Invalid date';
    }
  };

  // Show loading state when there's no data yet
  if (loading && (!appointments || appointments.length === 0)) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state if there's an error
  if (error) {
    // Safely extract error message
    let errorMessage = 'Failed to load appointments';
    
    if (error !== null && typeof error === 'object') {
      const errorObj = error as { message?: unknown };
      if (errorObj.message !== undefined) {
        errorMessage = String(errorObj.message);
      }
    }
    
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={() => window.location.reload()}>
              Retry
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      </Box>
    );
  }
  
  // Show no appointments message when there's no data
  if (!loading && (!appointments || appointments.length === 0 || total === 0)) {
    return (
      <Box textAlign="center" p={4} bgcolor="action.hover" borderRadius={1}>
        <Typography color="text.secondary">
          No appointments found for the selected criteria
        </Typography>
      </Box>
    );
  }

  // Show the appointments list
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" component="h2">
          Appointments
          <Typography component="span" color="text.secondary" ml={1}>
            ({total} total{total > 1 ? 's' : ''})
          </Typography>
        </Typography>
      </Box>

      {/* Show loading indicator if loading with existing data */}
      {loading && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={24} />
        </Box>
      )}

      <Grid container spacing={3}>
        {appointments.map((appointment) => {
          const statusInfo = getStatusInfo(appointment.status);
          const patientName = appointment.patient?.fullName || appointment.patientName || 'Unknown Patient';
          
          return (
            <Grid item xs={12} sm={6} md={4} key={appointment.id}>
              <Card 
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3,
                  },
                  borderLeft: `4px solid ${statusInfo.bgColor}`,
                }}
              >
                <CardHeader
                  avatar={
                    <Avatar 
                      sx={{ 
                        bgcolor: deepPurple[500],
                        width: 40,
                        height: 40,
                        fontSize: '1rem'
                      }}
                    >
                      {patientName.charAt(0).toUpperCase()}
                    </Avatar>
                  }
                  action={
                    <Tooltip title={statusInfo.label}>
                      <Chip 
                        icon={statusInfo.icon}
                        label={statusInfo.label}
                        size="small"
                        variant="outlined"
                        color={statusInfo.color}
                        sx={{ 
                          mt: 1, 
                          mr: 1,
                          bgcolor: statusInfo.bgColor,
                          border: 'none'
                        }}
                      />
                    </Tooltip>
                  }
                  title={
                    <Typography variant="subtitle1" fontWeight="medium">
                      {patientName}
                    </Typography>
                  }
                  subheader={
                    <Typography variant="caption" color="text.secondary">
                      {appointment.appointmentNumber}
                    </Typography>
                  }
                  sx={{ 
                    pb: 1,
                    '& .MuiCardHeader-content': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                    '& .MuiCardHeader-action': {
                      margin: 0,
                      alignSelf: 'center',
                    },
                  }}
                />
                
                <CardContent sx={{ pt: 0, pb: '16px !important' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <EventIcon color="action" fontSize="small" sx={{ mr: 1 }} />
                        {formatAppointmentDateTime(appointment.startTime)}
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="flex-start">
                        <LocalHospitalIcon color="action" fontSize="small" sx={{ mr: 1, mt: 0.5 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Doctor
                          </Typography>
                          {getDoctorInfo(appointment)}
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                    <Chip 
                      label={appointment.priority?.toLowerCase() || 'normal'}
                      size="small"
                      color={
                        appointment.priority === 'URGENT' ? 'error' :
                        appointment.priority === 'HIGH' ? 'warning' :
                        'default'
                      }
                      variant="outlined"
                    />
                    
                    <Button 
                      size="small" 
                      variant="outlined"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewAppointment(appointment);
                      }}
                      sx={{ ml: 'auto' }}
                    >
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      {/* Pagination */}
      <Box 
        mt={4} 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2,
          backgroundColor: 'background.paper',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
        }}
      >
        <TablePagination
          rowsPerPageOptions={[6, 10, 12, 24, 30, 48]}
          component="div"
          count={total}
          rowsPerPage={limit}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Rows per page:"
          labelDisplayedRows={({ from, to, count }: { from: number; to: number; count: number }) => {
            // Handle case when there are no items
            if (count === 0) return '0-0 of 0';
            
            // Calculate actual from and to values
            const actualFrom = count === 0 ? 0 : from;
            const actualTo = Math.min(to, count);
            
            return `${actualFrom}-${actualTo} of ${count}`;
          }}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexWrap: 'wrap',
              justifyContent: 'center',
              padding: 1,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              marginBottom: 1,
            },
            '& .MuiTablePagination-actions': {
              margin: '8px 0',
            },
            '& .MuiTablePagination-select': {
              margin: '0 8px',
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default AppointmentsList;
